import { useState } from 'react';
import { CreditCard, CheckCircle2, User, TrendingUp } from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';
import { useBdmFinancials } from './useBdmData';

interface BdmFinancialsCardProps {
  userId?: string;
  repName?: string;
}

export function BdmFinancialsCard({ userId, repName }: BdmFinancialsCardProps) {
  const { data, isLoading, isError } = useBdmFinancials(userId);
  const [showAllClients, setShowAllClients] = useState(false);

  const fin = data?.financials ?? {
    totalInvoiced: 0,
    totalCreditGiven: 0,
    totalRecovered: 0,
    pendingRecovery: 0,
    overdueRecovery: 0,
    recoveryRate: 100,
    activeCreditCustomers: 0,
  };

  const clients = data?.customerBreakdown ?? [];
  const displayedClients = showAllClients ? clients : clients.slice(0, 5);

  return (
    <div className="rounded-card border border-border bg-card p-5 shadow-xs transition hover:shadow-xs">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-container text-primary">
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-body-md font-semibold text-on-surface">
              {repName ? `${repName}'s Client Financials & Credit` : 'My Financials & Credit Recovery'}
            </h3>
            <p className="text-body-sm text-on-surface-variant">
              Credit given to customers vs. collected & pending for recovery
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-container px-3 py-1 text-body-sm font-semibold text-on-surface-variant">
            <User className="h-3.5 w-3.5 text-on-surface-variant" />
            {fin.activeCreditCustomers} {fin.activeCreditCustomers === 1 ? 'Customer' : 'Customers'} on Credit
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-body-sm text-on-surface-subtle">Loading sales financials...</div>
      ) : isError ? (
        <div className="py-8 text-center text-body-sm text-danger-text">Could not load financial metrics.</div>
      ) : (
        <div className="mt-4 space-y-4">
          {/* Main 4 Metric Cards */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {/* 1. Credit Given */}
            <div className="rounded-lg border border-info/30 bg-info/10 p-3.5">
              <span className="text-data-xs font-semibold uppercase tracking-wider text-info-text">Credit Extended</span>
              <div className="mt-1 text-data-lg font-semibold text-info-text tabular-nums">
                {formatINR(fin.totalCreditGiven)}
              </div>
              <p className="mt-0.5 text-data-xs text-info-text/80">Total invoiced on credit terms</p>
            </div>

            {/* 2. Total Recovered */}
            <div className="rounded-lg border border-success/30 bg-success/10 p-3.5">
              <span className="text-data-xs font-semibold uppercase tracking-wider text-success-text">Total Recovered</span>
              <div className="mt-1 text-data-lg font-semibold text-success-text tabular-nums">
                {formatINR(fin.totalRecovered)}
              </div>
              <p className="mt-0.5 text-data-xs text-success-text/80">Collections received to date</p>
            </div>

            {/* 3. Pending Recovery */}
            <div className="rounded-lg border border-warning/30 bg-warning/10 p-3.5">
              <span className="text-data-xs font-semibold uppercase tracking-wider text-warning-text">Pending Recovery</span>
              <div className="mt-1 text-data-lg font-semibold text-warning-text tabular-nums">
                {formatINR(fin.pendingRecovery)}
              </div>
              <p className="mt-0.5 text-data-xs text-warning-text">Balance outstanding from clients</p>
            </div>

            {/* 4. Overdue Balance */}
            <div className="rounded-lg border border-danger/30 bg-danger/5 p-3.5">
              <span className="text-data-xs font-semibold uppercase tracking-wider text-danger-text">Overdue Recovery</span>
              <div className="mt-1 text-data-lg font-semibold text-danger-text tabular-nums">
                {formatINR(fin.overdueRecovery)}
              </div>
              <p className="mt-0.5 text-data-xs text-danger-text/80">Exceeded payment due date</p>
            </div>
          </div>

          {/* Recovery Progress Bar */}
          <div className="rounded-lg border border-border bg-surface-variant/70 p-3">
            <div className="flex items-center justify-between text-body-sm">
              <span className="font-semibold text-on-surface-variant flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-primary" />
                Collection Recovery Efficiency
              </span>
              <span className="font-semibold text-on-surface tabular-nums">{fin.recoveryRate}%</span>
            </div>
            <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-surface-dim">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  fin.recoveryRate >= 80 ? 'bg-success' : fin.recoveryRate >= 50 ? 'bg-warning' : 'bg-danger'
                }`}
                style={{ width: `${Math.min(100, Math.max(0, fin.recoveryRate))}%` }}
              />
            </div>
          </div>

          {/* Customer Credit Breakdown List */}
          {clients.length > 0 ? (
            <div className="mt-4">
              <div className="flex items-center justify-between pb-2">
                <h4 className="text-body-sm font-semibold text-on-surface">
                  Customers with Pending Recovery
                </h4>
                {clients.length > 5 && (
                  <button
                    type="button"
                    onClick={() => setShowAllClients(!showAllClients)}
                    className="text-body-sm font-semibold text-primary hover:text-primary-dim"
                  >
                    {showAllClients ? 'Show Top 5' : `View All (${clients.length})`}
                  </button>
                )}
              </div>

              <div className="divide-y divide-border rounded-lg border border-border bg-card overflow-hidden">
                <div className="grid grid-cols-12 bg-surface-variant px-3 py-2 text-data-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                  <div className="col-span-5">Customer Name</div>
                  <div className="col-span-2 text-right">Credit Invoiced</div>
                  <div className="col-span-2 text-right">Recovered</div>
                  <div className="col-span-3 text-right">Pending Due</div>
                </div>

                {displayedClients.map((c) => (
                  <div key={c.customerId} className="grid grid-cols-12 items-center px-3 py-2.5 text-body-sm hover:bg-surface-variant/80 transition">
                    <div className="col-span-5 font-semibold text-on-surface truncate pr-2">
                      {c.customerName}
                    </div>
                    <div className="col-span-2 text-right font-medium text-on-surface-variant tabular-nums">
                      {formatINR(c.totalInvoiced)}
                    </div>
                    <div className="col-span-2 text-right font-medium text-success-text tabular-nums">
                      {formatINR(c.totalRecovered)}
                    </div>
                    <div className="col-span-3 text-right font-semibold text-warning-text tabular-nums">
                      {formatINR(c.pendingRecovery)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 rounded-lg bg-success/10 p-4 text-body-sm font-medium text-success-text">
              <CheckCircle2 className="h-4 w-4 text-success-text" />
              <span>Zero outstanding balance! All customer credits are fully settled and cleared.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
